import { useAuth0 } from "@auth0/auth0-react";
import styles from "./addExpense.module.css";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { TextField, FormControl, InputLabel, Select, MenuItem, Snackbar, Slide, IconButton, Typography, Dialog, DialogContent, DialogTitle, FormControlLabel, Checkbox, Tooltip } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import axios from "axios";
import { toast } from "react-smart-toaster";
import { useEffect, useState } from "react";
import NumberFormat from "react-number-format";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import Loader from "../../../../../../components/Loader";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { ArrowLeft, Home, Plus, X } from "react-feather";
import AddExpensePayee from "../../../../expense-payee/components/AddExpensePayee";
import { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";

const MoneyField = (props) => {
  return (<NumberFormat prefix="" displayType="input" type="text" thousandSeparator={true} allowNegative={false} decimalScale={2} fixedDecimalScale    {...props} />);
}

const MAX_FILE_SIZE_MB = 25;
const schema = yup.object().shape({
  property_id: yup.string().required("This field is Required").nullable(),
  apt_id: yup.string().nullable(),
  status: yup.boolean(),
  payee_id: yup.string().required("This field is Required").nullable(),
  category_id: yup.string().required("This field is Required").nullable(),
  due_date: yup.date().required("This field is Required"),
  description: yup.string().max(200, "Description cannot be more than 200 characters"),
  amount: yup.string().required("This field is Required").test("non-zero-validate", "This field should be $1 or greater.", (value) => parseInt(value) > 0).test("max-10-digit", "Amount can not be more than 10 digits.", (value) => { return parseInt(value?.replace(/,/g, "")) < 1000000000; })
});

const BootstrapTooltip = styled(({ className, ...props }) => (<Tooltip {...props} arrow classes={{ popper: className }} />))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.black,
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.black,
    fontSize: 13,
  },
}));

const AddExpense = ({ history, match }) => {
  const { getAccessTokenSilently, user, isAuthenticated } = useAuth0();
  const { id } = match?.params;
  const [categoryList, setCategoryList] = useState([]);
  const [allCategoryList, setAllCategoryList] = useState([]);
  const [propertyList, setPropertyList] = useState([]);
  const [apartmentList, setApartmentList] = useState([]);
  const [payeeList, setPayeeList] = useState([]);
  const [expenseDetails, setExpenseDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [imagefile, setImageFile] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);

  const { handleSubmit, control, errors, setValue, clearErrors } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues: {
      category_id: "",
      payee_id: "",
      property_id: "",
      apt_id: "",
      description: "",
      amount: "",
      status: false,
    },
  });

  useEffect(() => {
    const fetchAllProperties = async () => {
      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });
      axios(process.env.REACT_APP_PAYMENT_API_URL + "v1/property-list", {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      }).then((response) => {
        let propertyData = response.data.data;
        setPropertyList(propertyData);
      });
    }

    fetchAllProperties();
  }, [getAccessTokenSilently]);

  useEffect(() => {
    const getPayeeList = async () => {
      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

      axios(process.env.REACT_APP_PAYMENT_API_URL + "v1/payees", {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      }).then((response) => {
        setPayeeList(response.data.data);

        /*if (isNewPayeeAdded && response?.data?.data) {
          setTimeout(() => {
            const newPayee = response?.data?.data[0];
            setValue("payee_id", newPayee?.id);
            if (newPayee) {
              setCategoryList([newPayee.category]);
              setValue("category_id", newPayee?.category?.id);
            }
          }, 200);
        }*/
      }).catch(() => { });
    }
    getPayeeList();
  }, [getAccessTokenSilently]);

  useEffect(() => {
    const getCategoryList = async () => {
      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

      axios(process.env.REACT_APP_PAYMENT_API_URL + "v1/expense-categories", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }).then((response) => {
        setAllCategoryList(response.data.data);
      }).catch(() => { });
    }

    getCategoryList();
  }, [getAccessTokenSilently]);

  useEffect(() => {
    const getExpenseDetails = async () => {
      setLoading(true);
      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });
      axios(process.env.REACT_APP_PAYMENT_API_URL + "v1/expenses/" + id, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      }).then((response) => {
        setLoading(false);
        const responseData = response.data.data;
        setExpenseDetails(responseData);
      });
    }

    if (id) {
      getExpenseDetails();
    } else {
      setLoading(false);
    }
  }, [id, getAccessTokenSilently]);

  useEffect(() => {
    if (id) {
      setValue("property_id", expenseDetails?.property_id);
      setValue("apt_id", expenseDetails?.apt_id);
      setValue("status", expenseDetails?.status === 2 ? true : false);
      setValue("description", expenseDetails?.description);
      setValue("amount", expenseDetails?.amount);
      setValue("category_id", expenseDetails?.category_id);
      setValue("payee_id", expenseDetails?.payee_id);
      setValue("due_date", dayjs(expenseDetails?.due_date));

      const matchedProperty = propertyList?.find((i) => {
        return i.value === expenseDetails?.property_id;
      });

      const apartmentListTemp = matchedProperty?.apartments || [];
      setApartmentList(apartmentListTemp);
      const selectedPayee = payeeList.find((p) => p.category_id === expenseDetails?.category_id);
      if (selectedPayee) {
        setCategoryList([selectedPayee.category]);
        setValue("category_id", selectedPayee?.category?.id);
      }
    }
  }, [expenseDetails, payeeList, propertyList, id, setValue]);

  const onSubmit = async (data) => {
    setLoading(true);

    data.due_date = dayjs(data?.due_date).format("YYYY-MM-DD");
    data.amount = data.amount?.replace(/,/g, "");

    const formData = new FormData();
    formData.append("property_id", data.property_id);
    formData.append("description", data.description);
    formData.append("amount", data.amount);
    formData.append("due_date", data.due_date);
    formData.append("category_id", data.category_id);
    formData.append("payee_id", data.payee_id);
    formData.append("apt_id", data.apt_id);
    formData.append("status", data.status ? 2 : 0);
    if (imagefile) {
      formData.append("image", imagefile);
    }
    const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

    return axios.post(process.env.REACT_APP_PAYMENT_API_URL + "v1/expenses", formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      }
    }).then((response) => {
      setSnackbarOpen(true);
      setSnackbarMsg("Expense added successfully.");

      setLoading(false);

      setTimeout(() => {
        history.push("/expense/all-expense");
      }, 2000);
    }).catch((error) => {
      if (typeof error.response !== "undefined")
        toast.error(error.response.data.message);
      setLoading(false);
    });
  }

  const onUpdate = async (data) => {
    setLoading(true);

    const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

    data.due_date = dayjs(data?.due_date).format("YYYY-MM-DD");
    data.amount = data.amount?.replace(/,/g, "");

    const formData = new FormData();
    formData.append("property_id", data.property_id);
    formData.append("description", data.description);
    formData.append("amount", data.amount);
    formData.append("due_date", data.due_date);
    formData.append("category_id", data.category_id);
    formData.append("payee_id", data.payee_id);
    formData.append("apt_id", data.apt_id);
    formData.append("status", data.status ? 2 : 0);
    if (imagefile) {
      formData.append("image", imagefile);
    }

    return axios.post(process.env.REACT_APP_PAYMENT_API_URL + "v1/expenses/" + id, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      }
    }).then((response) => {
      setSnackbarOpen(true);
      setSnackbarMsg("Expenses updated successfully.");

      setLoading(false);
      setTimeout(() => {
        history.push("/expense/all-expense");
      }, 2000);
    }).catch((error) => {
      if (typeof error.response !== "undefined")
        toast.error(error.response.data.message);
      setLoading(false);
    });
  }

  const onAddNewPayee = (newPayee) => {
    //const isNewPayeeAdded = true;
    //getPayeeList(isNewPayeeAdded);

    setValue("payee_id", newPayee?.id);
    setCategoryList([newPayee?.category]);
    setValue("category_id", newPayee?.category?.id);

    setPayeeList(prev => {
      return [newPayee].concat(prev);
    });
  }

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const fileSizeInMB = file.size / (1024 * 1024);
      if (fileSizeInMB > MAX_FILE_SIZE_MB) {
        setSnackbarOpen(true);
        setSnackbarMsg("Image must be less than 25MB!");
        setValue("imageFile", null);
        setImageFile(null);
      } else {
        clearErrors("imageFile");
        setValue("imageFile", file);
        setImageFile(file);
      }
    }
  }

  return (<>
    {loading && <Loader />}

    <div className={`${styles.mainCard}`}>
      <div className={`${styles.SMProCusRow}`}>
        <div className={`${styles.BreadcrumbHeadSec}`}>
          <nav aria-label="breadcrumb">
            <ol className={`${styles.breadcrumb}`}>
              <li className={`${styles.breadcrumbItem}`}><span><Home /></span>{" "}Expenses</li>
              <li className={`${styles.breadcrumbItem} ${styles.active}`}>{id ? "Update" : "Add"}</li>
            </ol>
          </nav>
          <div className={`${styles.PageTitleSec}`}>
            <div>
              <Link to={`/expense/all-expense`} className={`${styles.BackArrow}`}><ArrowLeft /></Link>
              <Typography display="inline" className={`${styles.pageTitle}`}>{id ? "Update" : "Add"} Expenses</Typography>
            </div>
          </div>
        </div>

        <div className={`${styles.ExpensesAddMainSec}`}>
          <form onSubmit={handleSubmit(id ? onUpdate : onSubmit)}>
            <div className={`${styles.AddEspenseTopSec}`}>
              <div className={`${styles.FormGroupHalf} FormGroup`}>
                <FormControl fullWidth>
                  <InputLabel id="property-select-label">Property*</InputLabel>
                  <Controller
                    name="property_id"
                    control={control}
                    render={(field) => (<Select
                      {...field}
                      labelId="property-select-label"
                      id="property-select"
                      label="Property*"
                      onChange={(e) => {
                        field.onChange(e);

                        const matchedProperty = propertyList?.find((i) => i.value === e.target.value);
                        const apartmentListTemp = matchedProperty?.apartments || [];
                        setApartmentList(apartmentListTemp);
                      }}
                    >
                      {propertyList && propertyList.map((c, i) => (<MenuItem key={i} value={c.value}>{c.label}</MenuItem>))}
                    </Select>
                    )}
                  />
                </FormControl>
                {errors.property_id && (<p className={`${styles.ErrorM}`}>{errors.property_id.message}</p>)}
              </div>
              <div className={`${styles.FormGroupHalf} FormGroup`}>
                <FormControl fullWidth>
                  <InputLabel id="unit_id-select-label">Unit</InputLabel>
                  <Controller
                    name="apt_id"
                    control={control}
                    render={(field) => (<Select
                      {...field}
                      labelId="unit_id-select-label"
                      id="unit_id-select"
                      label="Unit"
                      onChange={(e) => {
                        field.onChange(e);
                      }}
                    >
                      {apartmentList && apartmentList.map((c, i) => (<MenuItem key={i} value={c.value}>{c.label}</MenuItem>))}
                    </Select>
                    )}
                  />
                  {errors.apt_id && (<p className={`${styles.ErrorM}`}>{errors.apt_id.message}</p>)}
                </FormControl>
              </div>
              <div className={`${styles.FormGroupHalf} FormGroup`}>
                <FormControl fullWidth>
                  <InputLabel id="unit_id-select-label">Payee*</InputLabel>
                  <Controller
                    name="payee_id"
                    control={control}
                    render={(field) => (<Select
                      {...field}
                      labelId="payee_id-select-label"
                      id="payee_id-select"
                      label="Payee*"
                      onChange={(e) => {
                        field.onChange(e);

                        const selectedId = e.target.value;
                        const selectedPayee = payeeList.find((p) => p.id === selectedId);
                        if (selectedPayee) {
                          setCategoryList([selectedPayee.category]);
                          setValue("category_id", selectedPayee?.category?.id);
                        }
                      }}
                    >
                      {payeeList && payeeList.map((c, i) => (<MenuItem key={i} value={c.id}>{c.company_name}</MenuItem>))}
                    </Select>)}
                  />
                </FormControl>
                {errors.payee_id && (<p className={`${styles.ErrorM}`}>{errors.payee_id.message}</p>)}
              </div>
              {isAuthenticated && user["https://propertydek.com/permissions"].indexOf("expense-payee:create") > -1 && <div className={`${styles.FormGroupHalf}`} onClick={(e) => { e.stopPropagation(); e.preventDefault(); setAddModalOpen(true); }}>
                <p className={`${styles.AddPayeeLink}`}>
                  <Plus /> Add New Payee
                </p>
              </div>}
            </div>
            <div className={`${styles.AddEspenseBottomSec}`}>
              <p className={`${styles.Title}`}>Expenses</p>
              <div className={`${styles.AddExpenseListSec}`}>
                <div className={`${styles.AddExpenseList}`}>
                  <div className={`${styles.AddEspenseDiv} FormGroup NoBG`}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <Controller
                        name="due_date"
                        control={control}
                        render={(field) => (<DatePicker
                          {...field}
                          label="Due Date*"
                          renderInput={(params) => (
                            <TextField {...params} />
                          )}
                        />)}
                      />
                    </LocalizationProvider>

                    {errors.due_date && (<p className={`${styles.ErrorM}`}>{errors?.due_date?.message}</p>)}
                  </div>
                  <div className={`${styles.AddEspenseDiv} FormGroup NoBG`}>
                    <FormControl fullWidth>
                      <InputLabel id="category-select-label">Category*</InputLabel>
                      <Controller
                        name="category_id"
                        control={control}
                        render={(field) => (<Select
                          {...field}
                          labelId="category-select-label"
                          id="category-select"
                          label="Category*"
                          onChange={(e) => field.onChange(e)}
                        >
                          {categoryList && categoryList.map((c, i) => (<MenuItem key={i} value={c.id}>{c.title}</MenuItem>))}
                        </Select>
                        )}
                      />
                    </FormControl>
                    {errors.category_id && (<p className={`${styles.ErrorM}`}>{errors.category_id.message}</p>)}
                  </div>
                  <div className={`${styles.AddEspenseDiv} FormGroup NoBG`}>
                    <Controller
                      name={`description`}
                      control={control}
                      render={(field) => (<TextField
                        onChange={(e) => field.onChange(e)}
                        id="outlined-basic"
                        label="Description"
                        variant="outlined"
                        value={field.value}
                      />)}
                    />
                    {errors.description && (<p className={`${styles.ErrorM}`}>{errors?.description?.message}</p>)}
                  </div>
                  <div className={`${styles.AddEspenseDiv} FormGroup NoBG`}>
                    <Controller
                      name={`amount`}
                      control={control}
                      render={(field) => (<TextField
                        {...field}
                        onChange={(e) => field.onChange(e)}
                        id="outlined-basic"
                        label="Amount"
                        variant="outlined"
                        value={field.value}
                        InputProps={{ inputComponent: MoneyField }}
                      />)}
                    />
                    {errors.amount && (<p className={`${styles.ErrorM}`}>{errors?.amount?.message}</p>)}
                  </div>
                  <div className={`${styles.AddEspenseDiv} ${styles.FileUpload} FormGroup`}>
                    <BootstrapTooltip title={<div className={`${styles.TooltipListRow}`}>
                      <div className={`${styles.TooltipList}`}>File size upto 25 MB</div>
                      <div className={`${styles.TooltipList}`}>JPEG, JPG, PNG only</div>
                    </div>}>
                      <div className={`${styles.AttachBUFile}`}>
                        <p>Attach a File</p>
                        <input type="file" accept="image/*" onChange={handleFileChange} />
                      </div>
                    </BootstrapTooltip>

                    {imagefile && (<img src={URL.createObjectURL(imagefile)} alt="Preview" style={{ width: 47, height: 47, objectFit: "cover", marginLeft: "12px" }} />)}

                    {!imagefile && expenseDetails && expenseDetails?.file_url && (<img src={expenseDetails?.file_url} alt="Preview" style={{ width: 47, height: 47, objectFit: "cover", marginLeft: "12px" }} />)}
                  </div>
                </div>
              </div>
              <Controller
                name="status"
                control={control}
                render={(field) => (<FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label="Mark as Paid."
                  className="CustomCheckLabel"
                />)}
              />
            </div>
            <div className={`${styles.AddBuSec}`}>
              <button type="button" onClick={(e) => { history.push("/expense/all-expense"); }} className={`${styles.CancelBu}`}>Cancel</button>
              <button className={`${styles.AddBu}`} type="submit">{id ? "Update" : "Add"}</button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <Dialog
      open={addModalOpen}
      onClose={(e) => { setAddModalOpen(false); }}
      fullWidth
      maxWidth="sm"
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className="NewPopUp MobileForm"
    >
      <DialogTitle id="alert-dialog-title">
        <button className={`${styles.BackAddBU}`} onClick={(e) => { setAddModalOpen(false); }}><ArrowLeft /></button>
        Add Payee
        <button onClick={(e) => { setAddModalOpen(false); }}><X /></button>
      </DialogTitle>
      <DialogContent>
        <AddExpensePayee
          setAddModalOpen={setAddModalOpen}
          setLoading={setLoading}
          setSnackbarOpen={setSnackbarOpen}
          setSnackbarMsg={setSnackbarMsg}
          setAllData={onAddNewPayee.bind(this)}
          editData={null}
          categoryList={allCategoryList}
        />
      </DialogContent>
    </Dialog>

    <Snackbar
      open={snackbarOpen}
      onClose={(e) => setSnackbarOpen(false)}
      TransitionComponent={Slide}
      message={snackbarMsg}
      autoHideDuration={10000}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      action={
        <IconButton
          size="small"
          aria-label="close"
          color="inherit"
          onClick={(e) => setSnackbarOpen(false)}
        >
          <X />
        </IconButton>
      }
    />
  </>);
};

export default withAuthenticationRequired(AddExpense, {
  onRedirecting: () => <Loader />,
});